import { useCallback, useEffect, useMemo, useState } from "react";
import type { DetailedPermitStage, Job } from "@/data/types";
import {
  PermittingPageService,
  type PageResponse,
  type PermittingJobsPageQuery,
  type PermittingQuickView,
  type PermittingSummary,
} from "@/services/permitting-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function usePermittingJobsPage(query: PermittingJobsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<Job>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void PermittingPageService.getJobsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [
    query.enabled,
    query.page,
    query.pageSize,
    query.search,
    query.stage,
    query.quickView,
    query.branch,
    query.region,
    query.refreshToken,
  ]);

  return { ...state, loading };
}

export function usePermittingSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<PermittingSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void PermittingPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function usePermittingServerData(query: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  stage?: DetailedPermitStage | "All";
  quickView?: PermittingQuickView;
  branch?: string;
  region?: string;
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = usePermittingJobsPage({
    enabled: query.enabled,
    page: query.page ?? 0,
    pageSize: query.pageSize ?? 25,
    search: query.search,
    stage: query.stage,
    quickView: query.quickView,
    branch: query.branch,
    region: query.region,
    refreshToken,
  });
  const summary = usePermittingSummary({ enabled: query.enabled, refreshToken });

  const serverJobs = useMemo(() => page.data, [page.data]);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    setJobs(serverJobs);
  }, [serverJobs]);

  const refresh = useCallback(async () => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    count: page.count,
    error: page.error ?? summary.error,
    loading: page.loading || summary.loading,
    jobs,
    setJobs,
    summary: summary.data,
    refresh,
  };
}
